import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { env } from '@/libs/env'
import { signout } from '@/apis/auth0'
import { Link } from 'react-router-dom'
import { AccountContext } from '@/contexts/account'
import { setEmployeeCurrentHotel } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useCurrentHotel } from '@/hooks/use-current-hotel'
import { useRestoreLanguage } from '@/hooks/use-restore-language'
import { CleanerLanguages } from '@/constants/cleaning-manager'

interface HeaderSpProps {
  // ヘッダー右側、メニューボタンの左に並べる画面固有の操作（一覧の並び替えなど）
  rightContent?: React.ReactNode
}

export const HeaderSp: React.FC<HeaderSpProps> = ({ rightContent }) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false)
  const { t, i18n } = useTranslation()
  const { account, employeeHotels } = useContext<any>(AccountContext)
  const { errorHandler } = useErrorHandler()
  const { hotelName } = useCurrentHotel()
  useRestoreLanguage(CleanerLanguages)

  const aipassZendeskUrl = env('AIPASS_ZENDESK_URL')

  const _logout = async () => {
    await signout()
  }

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    setIsOpenMenu(false)
  }

  const onClickHotel = async (hotel: any) => {
    try {
      await setEmployeeCurrentHotel(hotel.id)
      window.location.replace('/')
      setIsOpenMenu(false)
    } catch (error) {
      errorHandler(error)
    }
  }

  return (
    <header css={headerStyle}>
      <h1>
        <Link style={{ color: '#272727' }} to={'/cleaning'}>
          {hotelName}
        </Link>
      </h1>
      <div css={headerRightStyle}>
        {rightContent}
        <div className={`menu-button ${isOpenMenu ? 'open' : ''}`} onClick={() => setIsOpenMenu(!isOpenMenu)}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      {isOpenMenu && (
        <nav css={navMenuStyle} style={{ marginTop: 0 }}>
          <ul>
            {employeeHotels.length > 1 && (
              <li className="parent-item">
                {t('switch hotel')}
                <ul>
                  {employeeHotels.map((hotel: any, index: number) => (
                    <li
                      key={hotel.id}
                      onClick={() => onClickHotel(hotel)}
                      style={{ color: hotel.id === account?.hotel?.id ? '#F2A40B' : '#676767' }}
                    >
                      {hotel.name}
                    </li>
                  ))}
                </ul>
              </li>
            )}

            <li className="parent-item">
              Language
              <ul>
                <li onClick={() => changeLanguage('ja')} style={{ color: i18n.language === 'ja' ? '#F2A40B' : '#676767' }}>
                  日本語
                </li>
                <li onClick={() => changeLanguage('en')} style={{ color: i18n.language === 'en' ? '#F2A40B' : '#676767' }}>
                  English
                </li>
              </ul>
            </li>

            <a href={`${aipassZendeskUrl}/hc/ja`} target="_brank">
              <li>{t('Help page')}</li>
            </a>

            <li onClick={() => _logout()}>
              <p>{t('Logout')}</p>
            </li>
          </ul>
        </nav>
      )}
    </header>
  )
}

const headerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 56,
  paddingInline: 16,
  boxShadow: '0px 3px 6px #00000029',
  position: 'relative',
  zIndex: 201,
  h1: {
    // 省略の挙動は施設選択 UI に合わせる
    fontSize: 18,
    fontWeight: 'bold',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    a: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' },
  },
  '.menu-button': {
    width: 24,
    height: 24,
    position: 'relative',
    span: {
      width: '100%',
      height: 2,
      backgroundColor: '#F2A40B',
      position: 'absolute',
      '&:first-of-type': {
        top: 0,
      },
      '&:nth-of-type(2)': {
        top: 11,
      },
      '&:last-of-type': {
        bottom: 0,
      },
    },
    '&.open': {
      span: {
        '&:first-of-type': {
          transform: 'translateY(11px) rotate(-45deg)',
        },
        '&:nth-of-type(2)': {
          opacity: 0,
        },
        '&:last-of-type': {
          transform: 'translateY(-11px) rotate(45deg)',
        },
      },
    },
  },
})

const headerRightStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 18,
})

const navMenuStyle = css({
  position: 'absolute',
  top: 57,
  left: 0,
  width: '100%',
  height: 'calc(100vh - 56px)',
  padding: 24,
  backgroundColor: '#fff',
  zIndex: 200,
  li: {
    fontSize: 14,
    padding: '12px 0',
    color: '#272727',
    a: {
      color: '#272727',
    },
  },
  '.parent-item': {
    marginTop: 12,
    padding: 0,
    ul: {
      paddingTop: 12,
      li: { paddingLeft: 16 },
    },
  },
})
